import { useMemo } from 'react'

import { IconCheck, IconMagicSolid } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import contractsIllustration from '@webapp/platform/discover-more/assets/images/discover-more-contracts-illustration.png'
import customFieldsIllustration from '@webapp/platform/discover-more/assets/images/discover-more-custom-fields-illustration.png'
import guidedIntakeVideoAv1 from '@webapp/platform/discover-more/assets/videos/guided-intake-av1.mp4'
import guidedIntakeVideoH264 from '@webapp/platform/discover-more/assets/videos/guided-intake-h264.mp4'
import guidedIntakeVideoPoster from '@webapp/platform/discover-more/assets/videos/guided-intake-video-poster.jpg'
import vendorPortalVideoAv1 from '@webapp/platform/discover-more/assets/videos/vendorportal-promo-av1-primary.mp4'
import vendorPortalVideoH264 from '@webapp/platform/discover-more/assets/videos/vendorportal-promo-h264-fallback.mp4'
import vendorPortalVideoPoster from '@webapp/platform/discover-more/assets/videos/vendorportal-promo-poster.jpg'
import { type IFeatureShowcaseProps } from '@webapp/platform/discover-more/components/FeatureShowcase'
import { AP_HERO_VIDEO } from '@webapp/platform/discover-more/heroVideos'
import {
  type DiscoverMorePageTab,
  DiscoverMorePageTabs,
} from '@webapp/platform/discover-more/pages'
import { DISCOVER_MORE_FEATURE } from './constants'
import messages from './messages'

export const useDiscoverMoreFeatureContent = (
  tab: DiscoverMorePageTab
): IFeatureShowcaseProps | undefined => {
  const intl = useProcurifyIntl()

  return useMemo(() => {
    const demoRequestButtonText = intl.formatMessage(
      messages.requestDemoButtonText
    )
    const demoRequestDisclaimer = intl.formatMessage(messages.demoDisclaimer)

    const content: Partial<Record<DiscoverMorePageTab, IFeatureShowcaseProps>> =
      {
        [DiscoverMorePageTabs.VENDOR_PORTAL]: {
          header: intl.formatMessage(messages.vendorPortalHeader),
          description: intl.formatMessage(messages.vendorPortalDescription),
          proofPoints: [
            {
              icon: IconCheck,
              text: intl.formatMessage(
                messages.vendorPortalProofPointInviteProgress
              ),
            },
            {
              icon: IconCheck,
              text: intl.formatMessage(
                messages.vendorPortalProofPointMessaging
              ),
            },
            {
              icon: IconCheck,
              text: intl.formatMessage(
                messages.vendorPortalProofPointDashboard
              ),
            },
          ],
          heroVideo: {
            poster: vendorPortalVideoPoster,
            sources: [
              {
                src: vendorPortalVideoAv1,
                type: 'video/mp4; codecs="av01.0.08M.08.0.111.01.01.01.0"',
              },
              {
                src: vendorPortalVideoH264,
                type: 'video/mp4; codecs="avc1.640033"',
              },
            ],
            isExpandable: true,
          },
          heroImageAlt: intl.formatMessage(messages.vendorPortalHeroImageAlt),
          tourTitle: intl.formatMessage(messages.vendorPortalTabLabel),
          demoRequestContext:
            DISCOVER_MORE_FEATURE[DiscoverMorePageTabs.VENDOR_PORTAL],
          demoRequestDisclaimer,
          demoRequestButtonText: intl.formatMessage(
            messages.vendorPortalEarlyAccessButtonText
          ),
        },
        [DiscoverMorePageTabs.GUIDED_INTAKE]: {
          header: intl.formatMessage(messages.guidedIntakeHeader),
          description: intl.formatMessage(messages.guidedIntakeDescription),
          proofPoints: [
            {
              icon: IconMagicSolid,
              text: intl.formatMessage(
                messages.guidedIntakeProofPointPolicyMatch
              ),
            },
            {
              icon: IconMagicSolid,
              text: intl.formatMessage(
                messages.guidedIntakeProofPointAutomaticCoding
              ),
            },
            {
              icon: IconMagicSolid,
              text: intl.formatMessage(
                messages.guidedIntakeProofPointFlagsIssues
              ),
            },
          ],
          heroVideo: {
            poster: guidedIntakeVideoPoster,
            sources: [
              {
                src: guidedIntakeVideoAv1,
                type: 'video/mp4; codecs="av01.0.09M.08"',
              },
              {
                src: guidedIntakeVideoH264,
                type: 'video/mp4; codecs="avc1.64002A"',
              },
            ],
          },
          heroImageAlt: intl.formatMessage(messages.guidedIntakeHeroImageAlt),
          tourTitle: intl.formatMessage(messages.guidedIntakeTabLabel),
          demoRequestContext:
            DISCOVER_MORE_FEATURE[DiscoverMorePageTabs.GUIDED_INTAKE],
          demoRequestDisclaimer,
          demoRequestButtonText,
        },
        [DiscoverMorePageTabs.AP]: {
          header: intl.formatMessage(messages.apHeader),
          description: intl.formatMessage(messages.apDescription),
          proofPoints: [
            {
              icon: IconMagicSolid,
              text: intl.formatMessage(messages.apProofPointInvoiceMatching),
            },
            {
              icon: IconCheck,
              text: intl.formatMessage(messages.apProofPointGlobalPayments),
            },
            {
              icon: IconCheck,
              text: intl.formatMessage(messages.apProofPointErpSync),
            },
          ],
          heroVideo: AP_HERO_VIDEO,
          heroImageAlt: intl.formatMessage(messages.apHeroImageAlt),
          tourTitle: intl.formatMessage(messages.apTabLabel),
          demoRequestContext: DISCOVER_MORE_FEATURE[DiscoverMorePageTabs.AP],
          demoRequestDisclaimer,
          demoRequestButtonText,
        },
        [DiscoverMorePageTabs.CONTRACTS]: {
          header: intl.formatMessage(messages.contractsHeader),
          description: intl.formatMessage(messages.contractsDescription),
          proofPoints: [
            {
              icon: IconCheck,
              text: intl.formatMessage(
                messages.contractsProofPointCentralizedView
              ),
            },
            {
              icon: IconCheck,
              text: intl.formatMessage(
                messages.contractsProofPointRenewalAlerts
              ),
            },
            {
              icon: IconCheck,
              text: intl.formatMessage(
                messages.contractsProofPointLinkedPurchaseOrders
              ),
            },
          ],
          heroImage: contractsIllustration,
          heroImageAlt: intl.formatMessage(messages.contractsHeroImageAlt),
          tourTitle: intl.formatMessage(messages.contractsTabLabel),
          demoRequestContext:
            DISCOVER_MORE_FEATURE[DiscoverMorePageTabs.CONTRACTS],
          demoRequestDisclaimer,
          demoRequestButtonText,
        },
        [DiscoverMorePageTabs.CUSTOM_FIELDS]: {
          header: intl.formatMessage(messages.customFieldsHeader),
          description: intl.formatMessage(messages.customFieldsDescription),
          proofPoints: [],
          heroImage: customFieldsIllustration,
          heroImageAlt: intl.formatMessage(messages.customFieldsHeroImageAlt),
          tourTitle: intl.formatMessage(messages.customFieldsTabLabel),
          demoRequestContext:
            DISCOVER_MORE_FEATURE[DiscoverMorePageTabs.CUSTOM_FIELDS],
          demoRequestDisclaimer,
          demoRequestButtonText,
        },
      }

    return content[tab]
  }, [intl, tab])
}
